import { useState } from 'react';
import { api, ApiError } from '../api';
import { trackEvent } from '../analytics';
import { Logo } from '../components/BrandMarks';
import { useT } from '../i18n';

interface Props {
  navigate: (path: string) => void;
  onSignedIn: () => Promise<void>;
}

/**
 * Landing screen for the link in the login email.
 *
 * The confirming tap is what makes the link safe to email: mail scanners and
 * link-preview bots follow URLs but never POST, so they cannot burn the
 * single-use token before the customer arrives.
 */
export function ConfirmLogin({ navigate, onSignedIn }: Props) {
  const t = useT();
  const token = new URLSearchParams(window.location.search).get('token') ?? '';
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const confirm = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await api.emailLink(token);
      trackEvent('Auth', 'login_success', 'email_link');
      await onSignedIn();
      navigate(res.role === 'staff' ? '/admin' : '/perfil');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t.login.errorSignIn);
      setBusy(false);
    }
  };

  return (
    <div className="narrow screen">
      <div className="login-head">
        <Logo title="Caffeina" />
        <h1 className="login-title">{token ? t.confirm.title : t.confirm.brokenTitle}</h1>
      </div>

      {error && (
        <div className="notice notice--error" style={{ marginBottom: 16 }} role="alert">
          {error}
        </div>
      )}

      {token ? (
        <div className="provider-list">
          <p className="login-footnote" style={{ marginTop: 0 }}>
            {t.confirm.tapToEnter}
          </p>
          <button
            type="button"
            className="btn btn--solid"
            style={{ padding: 15, fontSize: 15 }}
            onClick={() => void confirm()}
            disabled={busy}
          >
            {busy ? t.confirm.entering : t.confirm.enter}
          </button>
        </div>
      ) : (
        <p className="login-footnote" style={{ marginTop: 0 }}>
          {t.confirm.broken}
        </p>
      )}

      <button
        type="button"
        className="btn--quiet"
        style={{ marginTop: 28, textTransform: 'none', fontSize: 12, letterSpacing: 0 }}
        onClick={() => navigate('/login')}
      >
        {t.confirm.backToLogin}
      </button>
    </div>
  );
}
